import { useMutation } from '@apollo/client/react'
import { Button, FieldMessage, InlineConfirmation, TextInput, useTranslate } from '@athena/ui'
import { sendStudentConnectionRequestInput } from '@athena/validation'
import { useRef, useState } from 'react'
import {
  SendStudentConnectionRequestDocument,
  TutorStudentConnectionRequestsDocument,
  MyStudentsDocument,
} from '../../graphql/generated/operations.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'

export interface AddStudentFormProps {
  onAdded?: (email: string) => void
  labelledBy: string
  pendingEmails?: readonly string[]
}
export function AddStudentForm({ onAdded, labelledBy, pendingEmails = [] }: AddStudentFormProps) {
  const t = useTranslate()
  const userErrorText = useFirstUserErrorText()
  const [email, setEmail] = useState('')
  const [fieldError, setFieldError] = useState<string>()
  const [error, setError] = useState<string>()
  const [confirmation, setConfirmation] = useState<string>()
  const input = useRef<HTMLInputElement>(null)
  const [send, { loading }] = useMutation(SendStudentConnectionRequestDocument, {
    refetchQueries: [TutorStudentConnectionRequestsDocument, MyStudentsDocument],
    awaitRefetchQueries: true,
  })
  const pending = pendingEmails.includes(email.trim().toLowerCase())
  return (
    <form
      aria-labelledby={labelledBy}
      noValidate
      className="flex w-full flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (pending || loading) return
        const parsed = sendStudentConnectionRequestInput.safeParse({ email })
        setError(undefined)
        setConfirmation(undefined)
        if (!parsed.success) {
          setFieldError(t('connections.emailError'))
          input.current?.focus()
          return
        }
        setFieldError(undefined)
        void (async () => {
          try {
            const result = await send({ variables: { email: parsed.data.email } })
            const payload = result.data?.sendStudentConnectionRequest
            if (!payload || payload.userErrors.length) {
              setError(userErrorText(payload?.userErrors, t('connections.failed')))
              return
            }
            setConfirmation(
              t(payload.alreadyConnected ? 'connections.alreadyConnected' : 'connections.sent'),
            )
            if (!payload.alreadyConnected && !payload.notificationSent)
              setError(t('connections.notificationFailed'))
            onAdded?.(parsed.data.email)
          } catch {
            setError(t('connections.failed'))
          }
        })()
      }}
    >
      <TextInput
        ref={input}
        type="email"
        autoComplete="email"
        required
        label={t('connections.email')}
        helperText={t('connections.emailHelp')}
        value={email}
        {...(fieldError ? { error: fieldError } : {})}
        onChange={(event) => {
          setEmail(event.target.value)
          setConfirmation(undefined)
          setError(undefined)
          setFieldError(undefined)
        }}
      />
      <div>
        <Button type="submit" loading={loading} disabled={pending}>
          {t(pending ? 'connections.pending' : 'connections.send')}
        </Button>
      </div>
      {confirmation ? <InlineConfirmation>{confirmation}</InlineConfirmation> : null}
      {error ? <FieldMessage tone="error">{error}</FieldMessage> : null}
    </form>
  )
}
